import { FormInstance, Tag } from "antd";
import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateValue,
  QueryFieldMapping,
} from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import {
  createSearchRequest,
  createScopedFilterRequest,
  createStaticOptionsRequest,
} from "../../hooks/useSearchRequest";
import {
  getRewards,
  getBadges,
  getCompanies,
  getCompanyDepartments,
  getPlatformUsers,
  getCompanyRewards,
  getDepartmentsRewards,
  getUsersReward,
  getCompanyBadges,
  getDepartmentsBadges,
  getUsersBadges,
  getWellbeingMaterials,
  getMaterialCompanyAssignments,
  getMaterialDepartmentAssignments,
  getMaterialUserAssignments,
} from "../../repository/data.repository";

export type AssignmentVariant = "reward" | "badge" | "material";
export type AssignmentTarget = "company" | "department" | "user";

// Required is a material ("required" kind) boolean filter, mirroring the Required column.
const requiredRequest = createStaticOptionsRequest([
  { display: "Required", value: "true" },
  { display: "Optional", value: "false" },
]);

interface TargetConfig {
  readonly getFn: (input: any) => Promise<any>;
  readonly colTitle: string;
  readonly colKey: string;
  // The filter controls for this target. Company is a flat top-level picker; Department is scoped to the
  // chosen Company; User is scoped to the chosen Company + Department (soft scope — platform-wide by default).
  readonly filterControls: (form: FormInstance<any>) => any[];
  // Only the target's own field is an actual query condition; parent fields above are scoping-only.
  readonly queryMappings: QueryFieldMapping[];
}

interface VariantConfig {
  readonly entityTitle: string;
  readonly entityKey: string;
  readonly permission: string;
  // "expiry" = reward/badge rows (Expiration Date + Active columns); "required" = material rows
  // (Required column instead). Drives the variant-specific grid columns.
  readonly kind: "expiry" | "required";
  readonly entityFilter: {
    readonly name: string;
    readonly label: string;
    readonly queryField: string;
    readonly request: (search: string) => Promise<any[]>;
  };
  readonly targets: Record<AssignmentTarget, TargetConfig>;
}

// Reusable scoped-filter control builders (shared by both variants).
const companyControl = () => ({
  type: "combobox" as const,
  name: "company",
  label: "Company",
  request: createSearchRequest(getCompanies, "name"),
});
const departmentControl = (form: FormInstance<any>) => ({
  type: "combobox" as const,
  name: "department",
  label: "Department",
  dependsOn: ["company"],
  request: createScopedFilterRequest(getCompanyDepartments, "name", () => [
    ["companyUid", form.getFieldValue("company")?.value],
  ]),
});
const userControl = (form: FormInstance<any>) => ({
  type: "combobox" as const,
  name: "user",
  label: "User",
  dependsOn: ["company", "department"],
  request: createScopedFilterRequest(getPlatformUsers, "userName", () => [
    ["companyUid", form.getFieldValue("company")?.value],
    ["departmentUid", form.getFieldValue("department")?.value],
  ]),
});

const companyTarget = (getFn: (input: any) => Promise<any>): TargetConfig => ({
  getFn,
  colTitle: "Company",
  colKey: "companyName",
  filterControls: () => [companyControl()],
  queryMappings: [
    { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  ],
});
const departmentTarget = (getFn: (input: any) => Promise<any>): TargetConfig => ({
  getFn,
  colTitle: "Department",
  colKey: "departmentName",
  filterControls: (form) => [companyControl(), departmentControl(form)],
  queryMappings: [
    { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  ],
});
const userTarget = (getFn: (input: any) => Promise<any>): TargetConfig => ({
  getFn,
  colTitle: "User Name",
  colKey: "userUserName",
  filterControls: (form) => [companyControl(), departmentControl(form), userControl(form)],
  queryMappings: [
    { formField: "user", queryField: "userUid", operation: QueryOperation.Equal, transform: comboboxValue },
  ],
});

export const assignmentConfig: Record<AssignmentVariant, VariantConfig> = {
  reward: {
    entityTitle: "Reward",
    entityKey: "rewardName",
    permission: "RewardsAssignment",
    kind: "expiry",
    entityFilter: {
      name: "reward",
      label: "Reward",
      queryField: "rewardUid",
      request: createSearchRequest(getRewards, "name"),
    },
    targets: {
      company: companyTarget(getCompanyRewards),
      department: departmentTarget(getDepartmentsRewards),
      user: userTarget(getUsersReward),
    },
  },
  badge: {
    entityTitle: "Badge",
    entityKey: "badgeName",
    permission: "BadgesAssignment",
    kind: "expiry",
    entityFilter: {
      name: "badge",
      label: "Badge",
      queryField: "badgeUid",
      request: createSearchRequest(getBadges, "name"),
    },
    targets: {
      company: companyTarget(getCompanyBadges),
      department: departmentTarget(getDepartmentsBadges),
      user: userTarget(getUsersBadges),
    },
  },
  material: {
    entityTitle: "Material",
    entityKey: "materialName",
    permission: "WellbeingCornerAssignment",
    kind: "required",
    entityFilter: {
      name: "material",
      label: "Material",
      queryField: "materialUid",
      request: createSearchRequest(getWellbeingMaterials, "name"),
    },
    targets: {
      company: companyTarget(getMaterialCompanyAssignments),
      department: departmentTarget(getMaterialDepartmentAssignments),
      user: userTarget(getMaterialUserAssignments),
    },
  },
};

export function assignmentColumns(
  variant: VariantConfig,
  target: TargetConfig,
): DataColumn[] {
  // Reward/badge rows carry Expiration Date + Active; material rows carry a Required flag instead.
  const variantCols: DataColumn[] =
    variant.kind === "required"
      ? [
          {
            title: "Required",
            key: "isRequired",
            width: 120,
            sort: true,
            transform: (value: any) => (
              <Tag color={value ? "red" : "default"}>
                {value ? "Required" : "Optional"}
              </Tag>
            ),
          },
        ]
      : [
          { title: "Expiration Date", key: "expirationDate", width: 168, format: "DD/MM/YYYY", sort: true },
          {
            title: "Active",
            key: "isActive",
            width: 110,
            sort: true,
            transform: (value: any) => (
              <Tag color={value ? "green" : "default"}>{value ? "Active" : "Inactive"}</Tag>
            ),
          },
        ];

  return [
    { title: variant.entityTitle, key: variant.entityKey, width: 192, sort: true, defaultSort: "asc", fixed: "left" },
    { title: target.colTitle, key: target.colKey, width: 192, sort: true },
    { title: "Assigned By", key: "assignedByUserUserName", width: 192, sort: true },
    { title: "Assignment Date", key: "assignmentDate", width: 168, format: "DD/MM/YYYY", sort: true },
    ...variantCols,
    { title: "Created On", key: "createdOn", width: 168, format: "DD/MM/YYYY HH:mm", sort: true },
  ];
}

// Factory so the target's scoped filter controls can read sibling values off the grid's own form.
export function assignmentSchema(
  variant: VariantConfig,
  target: TargetConfig,
): (form: FormInstance<any>) => DynamicSchema {
  return (form) => ({
    groups: [
      {
        controls: [
          { type: "combobox", name: variant.entityFilter.name, label: variant.entityFilter.label, request: variant.entityFilter.request },
          ...target.filterControls(form),
          { type: "textbox", name: "assignedByUserUserName", label: "Assigned By" },
          { type: "datepicker", name: "assignmentDate", label: "Assignment Date" },
          // Expiration is a reward/badge-only concept ("expiry" kind); material rows have no expiration.
          ...(variant.kind === "expiry"
            ? [
                {
                  type: "datepicker" as const,
                  name: "expirationDate",
                  label: "Expiration Date",
                },
              ]
            : []),
          // Required is the material ("required" kind) counterpart filter to the Required column.
          ...(variant.kind === "required"
            ? [
                {
                  type: "combobox" as const,
                  name: "isRequired",
                  label: "Required",
                  request: requiredRequest,
                },
              ]
            : []),
        ],
      },
    ],
  });
}

export function mapAssignmentQuery(
  variant: VariantConfig,
  target: TargetConfig,
): (values: any) => SearchQuery {
  const mappings: QueryFieldMapping[] = [
    { formField: variant.entityFilter.name, queryField: variant.entityFilter.queryField, operation: QueryOperation.Equal, transform: comboboxValue },
    ...target.queryMappings,
    { formField: "assignedByUserUserName" },
    { formField: "assignmentDate", operation: QueryOperation.Equal, transform: dateValue },
    // Expiration filter only exists for reward/badge ("expiry") rows — see assignmentSchema.
    ...(variant.kind === "expiry"
      ? [
          {
            formField: "expirationDate",
            operation: QueryOperation.Equal,
            transform: dateValue,
          },
        ]
      : []),
    // Required filter only exists for material ("required") rows.
    ...(variant.kind === "required"
      ? [
          {
            formField: "isRequired",
            queryField: "isRequired",
            operation: QueryOperation.Equal,
            transform: comboboxValue,
          },
        ]
      : []),
  ];
  return (values: any) => buildSearchQuery(values, mappings);
}
